"use client"

import { canonicalizeModalQuery, isModalName, type ModalName } from "./registry"
import {
  buildModalParamsSearch,
  buildModalSearch,
  hasModalQuery,
  modalPropsFromSearch,
  MODAL_QUERY,
  stripModalSearch
} from "./url-query"

export { MODAL_QUERY }

type PopstateGlobal = typeof globalThis & {
  __powerbetUrlModalPopstate?: () => void
}

let openedByApp = false
let popstateBound = false
const urlSearchListeners = new Set<() => void>()

function currentSearch(): string {
  if (typeof window === "undefined") return ""
  return window.location.search.startsWith("?")
    ? window.location.search.slice(1)
    : window.location.search
}

export function getUrlSearch() {
  return currentSearch()
}

export function subscribeUrlSearch(listener: () => void) {
  urlSearchListeners.add(listener)
  if (typeof window !== "undefined") {
    window.addEventListener("popstate", listener)
  }
  return () => {
    urlSearchListeners.delete(listener)
    if (typeof window !== "undefined") {
      window.removeEventListener("popstate", listener)
    }
  }
}

function emitUrlChange() {
  for (const listener of urlSearchListeners) listener()
}

// The hash is carried through: every write here replaces the whole URL, so
// dropping it would silently unanchor the page under the modal.
function hrefFromSearch(search: string): string {
  const { pathname, hash } = window.location
  return search ? `${pathname}?${search}${hash}` : `${pathname}${hash}`
}

function onPopState() {
  openedByApp = false
  if (!syncCanonicalModalSearch()) {
    emitUrlChange()
  }
}

export function syncCanonicalModalSearch(): boolean {
  if (typeof window === "undefined") return false

  const current = currentSearch()
  const name = new URLSearchParams(current).get(MODAL_QUERY)
  if (!isModalName(name)) return false

  const canonicalize = canonicalizeModalQuery[name]
  if (!canonicalize) return false

  const search = buildModalParamsSearch(
    current,
    canonicalize(modalPropsFromSearch(current))
  )
  if (search === current) return false

  window.history.replaceState(window.history.state, "", hrefFromSearch(search))
  emitUrlChange()
  return true
}

function bindPopstateListener() {
  if (popstateBound || typeof window === "undefined") {
    return
  }
  popstateBound = true

  const g = globalThis as PopstateGlobal
  const previous = g.__powerbetUrlModalPopstate
  if (previous) {
    window.removeEventListener("popstate", previous)
  }

  window.addEventListener("popstate", onPopState)
  g.__powerbetUrlModalPopstate = onPopState
}

bindPopstateListener()

export function resolveModalOpenParams(
  name: ModalName,
  params?: Record<string, string>
): Record<string, string> | undefined {
  const canonicalize = canonicalizeModalQuery[name]
  if (!canonicalize) return params
  return { ...params, ...canonicalize(params ?? {}) }
}

export function openUrlModal(name: ModalName, params?: Record<string, string>) {
  if (!isModalName(name)) return
  if (typeof window === "undefined") return

  const current = currentSearch()
  const search = buildModalSearch(
    current,
    name,
    resolveModalOpenParams(name, params)
  )
  const url = hrefFromSearch(search)
  const state = window.history.state

  if (hasModalQuery(current)) {
    window.history.replaceState(state, "", url)
  } else {
    window.history.pushState(state, "", url)
  }
  openedByApp = true
  emitUrlChange()
}

// Navigation *inside* an open modal (a tab, a payment method). Deliberately
// not openUrlModal: that one flips openedByApp, so switching a tab after a
// deep link would make the next close call history.back() and leave the site.
// A tab is not a history step either — Back closes the whole modal.
function withCanonicalParams(
  current: string,
  params: Record<string, string | undefined>,
  canonicalize: (raw: Record<string, string>) => Record<string, string>
): Record<string, string | undefined> {
  const stripped = Object.fromEntries(
    Object.entries({ ...modalPropsFromSearch(current), ...params }).filter(
      (entry): entry is [string, string] => entry[1] !== undefined
    )
  )
  return { ...params, ...canonicalize(stripped) }
}

export function setUrlModalParams(params: Record<string, string | undefined>) {
  if (typeof window === "undefined") return
  const current = currentSearch()
  if (!hasModalQuery(current)) return

  const name = new URLSearchParams(current).get(MODAL_QUERY)
  const canonicalize = isModalName(name)
    ? canonicalizeModalQuery[name]
    : undefined
  const nextParams = canonicalize
    ? withCanonicalParams(current, params, canonicalize)
    : params

  const search = buildModalParamsSearch(current, nextParams)
  window.history.replaceState(window.history.state, "", hrefFromSearch(search))
  emitUrlChange()
}

function replaceModalSearch(ownKeys?: readonly string[]) {
  const search = stripModalSearch(currentSearch(), ownKeys)
  window.history.replaceState(window.history.state, "", hrefFromSearch(search))
  emitUrlChange()
}

// Sign-in must not history.back(): that entry still holds the guest router
// cache, so the header flips back to INGRESAR every other attempt.
export function dismissUrlModal(ownKeys?: readonly string[]) {
  if (typeof window === "undefined") return
  openedByApp = false
  replaceModalSearch(ownKeys)
}

export function closeUrlModal(ownKeys?: readonly string[]) {
  if (typeof window === "undefined") {
    return
  }

  if (openedByApp) {
    openedByApp = false
    window.history.back()
    return
  }

  replaceModalSearch(ownKeys)
}
